import { useMemo, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type {
  MasterControlDangerActions,
  MasterControlWorkflowEntry,
  MasterControlWorkflowState,
} from "@/hooks/useMasterControl";
import MCWorkflowSummaryCards from "./MCWorkflowSummaryCards";
import MCWorkflowInspectorTable from "./MCWorkflowInspectorTable";
import MCWorkflowInspectorDrawer from "./MCWorkflowInspectorDrawer";
import { Button } from "@/components/ui/button";

type MCWorkflowInspectorProps = {
  state: MasterControlWorkflowState;
  loading: boolean;
  onRefresh: () => Promise<void>;
  dangerActions: MasterControlDangerActions;
  canReset: boolean;
};

export function MCWorkflowInspector({ state, loading, onRefresh, dangerActions, canReset }: MCWorkflowInspectorProps) {
  const active = state?.active ?? [];
  const stalled = state?.stalled ?? [];
  const pending = state?.pendingTasks ?? [];
  const stats = state?.stats;

  const [selected, setSelected] = useState<MasterControlWorkflowEntry | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleSelect = (entry: MasterControlWorkflowEntry) => {
    setSelected(entry);
    setDrawerOpen(true);
  };

  const handleReset = async () => {
    if (!canReset) return;
    setResetting(true);
    try {
      await dangerActions.resetWorkflowEngine();
      await onRefresh();
      setDrawerOpen(false);
      setSelected(null);
    } finally {
      setResetting(false);
    }
  };

  const emptyState = !loading && !active.length && !stalled.length && !pending.length;

  const sections = useMemo(
    () => [
      {
        title: "Active workflows",
        description: "Runs currently progressing or awaiting HITL.",
        rows: active,
        empty: "No active workflows.",
        showStalled: false,
      },
      {
        title: "Stalled workflows",
        description: "Runs needing intervention, inspections, or restarts.",
        rows: stalled,
        empty: "No stalled workflows detected.",
        showStalled: true,
      },
      {
        title: "Pending human-review tasks",
        description: "Manual approvals and judgments queued for LangGraph.",
        rows: pending,
        empty: "No pending tasks.",
        showStalled: false,
      },
    ],
    [active, stalled, pending],
  );

  return (
    <Card className="rounded-2xl border border-border bg-background/60 shadow-none">
      <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <CardTitle className="text-lg font-semibold text-foreground">Workflow Inspector</CardTitle>
          <p className="text-sm text-muted-foreground">
            LangGraph state, stalled workflow diagnostics, and pending human-review actions.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void onRefresh()} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <MCWorkflowSummaryCards stats={stats ?? undefined} active={active} stalled={stalled} pending={pending} />
        {emptyState ? (
          <p className="text-sm text-muted-foreground">No workflow data available.</p>
        ) : (
          sections.map((section) => (
            <details key={section.title} open className="rounded-2xl border border-border bg-background/40 p-4">
              <summary className="cursor-pointer text-sm font-semibold text-foreground">
                {section.title}
                <span className="ml-2 text-xs text-muted-foreground">{section.description}</span>
              </summary>
              <div className="mt-4">
                <MCWorkflowInspectorTable
                  rows={section.rows}
                  emptyMessage={section.empty}
                  showStalledInfo={section.showStalled}
                  onSelect={handleSelect}
                />
              </div>
            </details>
          ))
        )}
      </CardContent>

      <MCWorkflowInspectorDrawer
        entry={selected}
        open={drawerOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDrawerOpen(false);
            setSelected(null);
          } else {
            setDrawerOpen(true);
          }
        }}
        onReset={handleReset}
        resetting={resetting}
        canReset={canReset}
      />
    </Card>
  );
}

export default MCWorkflowInspector;
